import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AllCommunityModule,
  CellSelectionOptions,
  ColDef,
  GetRowIdParams,
  ModuleRegistry,
  RowSelectionOptions,
  Theme,
  themeQuartz
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Stack, LinearProgress, Typography, Button } from "@mui/material";
import { fetchGraphQL } from "../../Graphql/utils";
import AlertBannerContext from "../../Contexts/alertBannerContext";
import WithAuth from "../../Hoc/WithAuth";
import {
  adminGetEmployeeListRequest,
  Employee,
  GetEmployeeListResponse
} from "../../Graphql/types/employee";
import { adminGetAllEmployeeListQuery } from "../../Graphql/querries/employeeQuery";
import ViewEmployeeDetailButton from "./ViewEmployeeDetailButton";
import { EmployeeSearchConfig } from "./config/employeeSearchConfig";
import SearchArea from "../Common/Search/SearchArea";
import { CREATE_EMPLOYEE } from "../Routes";

ModuleRegistry.registerModules([AllCommunityModule]);

const EmployeeList = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const [rowData, setRowData] = useState<Employee[]>([]);
  const [columnDefs, setColumnDefs] = useState<ColDef[]>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const containerStyle = useMemo(
    () => ({ width: "100%", height: "calc(100vh - 100px)" }),
    []
  );
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const myTheme = themeQuartz.withParams({
    // color and style of border around selection
    rangeSelectionBorderColor: "rgb(193, 0, 97)",
    rangeSelectionBorderStyle: "dashed",
    // background color of selection - you can use a semi-transparent color
    // and it wil overlay on top of the existing cells
    rangeSelectionBackgroundColor: "rgb(255, 0, 128, 0.1)",
    // color used to indicate that data has been copied form the cell range
    rangeSelectionHighlightColor: "rgb(60, 188, 0, 0.3)"
  });

  const theme = useMemo<Theme | "legacy">(() => {
    return myTheme;
  }, []);

  const { mutate, isPending } = useMutation<
    GetEmployeeListResponse,
    Error,
    adminGetEmployeeListRequest
  >({
    mutationKey: ["(adminGetAllEmployeeList"],
    mutationFn: (input) =>
      fetchGraphQL(adminGetAllEmployeeListQuery, { input }, setAlert),
    onSuccess: (response) => {
      setRowData(response.adminGetAllEmployees || []);
    },
    onError: () => {
      setRowData([]);
    }
  });

  const rowSelection: RowSelectionOptions = {
    mode: "singleRow",
    checkboxes: false
  };

  const cellSelection = useMemo<boolean | CellSelectionOptions>(() => {
    return { handle: { mode: "range" } };
  }, []);

  useEffect(() => {
    setColumnDefs([
      { field: "firstName" },
      { field: "lastName" },
      { field: "alias" },
      { field: "hiredEmployeeInfo.positions", headerName: "Positions" },
      { field: "hiredEmployeeInfo.positionLevel", headerName: "Level" },
      { field: "role" },
      { field: "email" },
      { field: "phoneNumber" },
      {
        field: "actions",
        headerName: "Actions",
        cellRenderer: ViewEmployeeDetailButton
      }
    ]);
  }, []);

  const getRowId = useCallback(
    (params: GetRowIdParams) => String(params.data.id),
    []
  );

  useEffect(() => {
    const queryParams: adminGetEmployeeListRequest = {
      employeeId: searchParams.get("employeeId") ?? undefined,
      email: searchParams.get("email") ?? undefined,
      phoneNumber: searchParams.get("phoneNumber") ?? undefined,
      alias: searchParams.get("alias") ?? undefined,
      firstName: searchParams.get("firstName") ?? undefined,
      lastName: searchParams.get("lastName") ?? undefined
    };

    mutate({ ...queryParams });
  }, [searchParams]);

  const handleCreateEmployee = () => {
    navigate(CREATE_EMPLOYEE);
  };

  const defaultColDef = useMemo(() => {
    return {
      filter: true,
      floatingFilter: true,
      flex: 1,
      minWidth: 100,
      resizable: true
    };
  }, []);

  return (
    <div style={containerStyle}>
      <SearchArea formConfig={EmployeeSearchConfig} />
      {isPending && (
        <Stack width="100%" paddingBottom="1em" alignSelf="center">
          <LinearProgress />
        </Stack>
      )}
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "row",
          width: "100%",
          padding: "1em"
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Employee Listing
        </Typography>
      </Stack>
      <Stack
        style={{
          display: "flex",
          justifyContent: "right",
          flexDirection: "row",
          width: "100%",
          padding: "2em",
          boxSizing: "border-box"
        }}
      >
        <Button variant="contained" onClick={handleCreateEmployee}>
          Create new Employee
        </Button>
      </Stack>
      <div style={gridStyle}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection={rowSelection}
          pagination
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 25, 50]}
          theme={theme}
          cellSelection={cellSelection}
          getRowId={getRowId}
        />
      </div>
    </div>
  );
};

export default WithAuth(EmployeeList);
